import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Save and reset for a long form, following the reader down the page.
 *
 * The buttons render in place at the end of the form; once that footer
 * scrolls out of view the same pair reappears in a floating bar, so a change
 * made at the top of a long page never leaves the save button somewhere the
 * reader has to go looking for.
 *
 * Extracted from the business tab, which had it, so the agent settings tab —
 * which did not — behaves the same way rather than approximately the same way.
 *
 * Both are disabled until something has actually changed. Previously neither
 * knew whether there was anything to save, so the button invited a pointless
 * write and gave no signal that an edit had registered.
 */
interface StickySaveBarProps {
  isDirty: boolean;
  isSaving: boolean;
  onSave: () => void;
  onReset: () => void;
  saveLabel: string;
  /** Hidden entirely in read-only states. */
  hidden?: boolean;
}

export const StickySaveBar: React.FC<StickySaveBarProps> = ({
  isDirty,
  isSaving,
  onSave,
  onReset,
  saveLabel,
  hidden = false,
}) => {
  const { t } = useTranslation();
  const footerRef = useRef<HTMLDivElement>(null);
  const [isFooterInView, setIsFooterInView] = useState(false);

  useEffect(() => {
    if (hidden) return;
    const footer = footerRef.current;
    if (!footer) return;

    // The page scrolls inside <main>, not the window, so the observer has to
    // watch that element or it never fires.
    const scrollRoot = footer.closest('main');
    const observer = new IntersectionObserver(
      ([entry]) => setIsFooterInView(entry.isIntersecting),
      {
        root: scrollRoot instanceof Element ? scrollRoot : null,
        threshold: 0.4,
      }
    );
    observer.observe(footer);
    return () => observer.disconnect();
  }, [hidden]);

  if (hidden) return null;

  const actions = (
    <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-end sm:gap-3">
      <Button
        variant="transparent"
        size="sm"
        onClick={onReset}
        disabled={isSaving || !isDirty}
        className="shrink-0 px-3"
      >
        {t('agent.business_hours.reset.translation', 'Reset changes')}
      </Button>
      <Button
        variant="brand"
        onClick={onSave}
        disabled={isSaving || !isDirty}
        className="shrink-0"
      >
        {isSaving ? t('common.saving.translation', 'Saving...') : saveLabel}
      </Button>
    </div>
  );

  return (
    <>
      <div ref={footerRef} className="flex justify-end">
        {actions}
      </div>

      {!isFooterInView && (
        <div
          className={cn(
            'pointer-events-none fixed inset-x-0 z-40 flex justify-center px-4',
            'max-lg:bottom-[calc(4.5rem+env(safe-area-inset-bottom,0px))] lg:bottom-6 lg:pl-20'
          )}
        >
          <div className="glass-surface pointer-events-auto flex w-full max-w-4xl justify-end rounded-xl p-3 sm:px-4">
            {actions}
          </div>
        </div>
      )}
    </>
  );
};

export default StickySaveBar;
